import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, User, DollarSign } from "lucide-react";
import { DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { DailyWorkRateProfileRepository } from "@/repositories/daily-work-rate-profile.repository";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AssignableIndividual {
  id: string;
  name: string;
  organizationIndividualId: string;
}

type AssignmentRateSource = "profile" | "manual" | "none";

interface IndividualAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: DailyWorkDayJoin;
  onAssign: (assignment: {
    individualId: string;
    payment: number;
  }) => Promise<void>;
  isLoading?: boolean;
}

export default function IndividualAssignmentModal({
  isOpen,
  onClose,
  day,
  onAssign,
  isLoading = false,
}: IndividualAssignmentModalProps) {
  const [selectedIndividual, setSelectedIndividual] = useState<string>("");
  const [payment, setPayment] = useState<number>(day.individual_payment || 0);
  const [rateSource, setRateSource] = useState<AssignmentRateSource>(day.individual_payment_source ?? "none");
  const [individuals, setIndividuals] = useState<AssignableIndividual[]>([]);
  const [loadingIndividuals, setLoadingIndividuals] = useState(false);

  // Load individuals when modal opens
  useEffect(() => {
    if (isOpen) {
      loadIndividuals();
      // Pre-select current individual if assigned
      if (day.assigned_user) {
        setSelectedIndividual(day.assigned_user.id);
        setPayment(day.individual_payment || 0);
        setRateSource(day.individual_payment_source ?? "manual");
      } else {
        setSelectedIndividual("__none__");
        setPayment(0);
        setRateSource("none");
      }
    } else {
      // Reset state when modal closes
      setSelectedIndividual("__none__");
      setPayment(0);
      setRateSource("none");
    }
  }, [isOpen, day]);

  const loadIndividuals = async () => {
    setLoadingIndividuals(true);
    try {
      const assignableIndividuals = await IndividualsRepository.listForAssignment();
      setIndividuals(assignableIndividuals);
    } catch (error) {
      console.error("Failed to load individuals:", error);
      setIndividuals([]);
    } finally {
      setLoadingIndividuals(false);
    }
  };

  const handleClose = () => {
    setSelectedIndividual("__none__");
    setPayment(0);
    setRateSource("none");
    onClose();
  };

  const applyProfileRate = (individualId: string) => {
    if (!individualId || individualId === "__none__") {
      setPayment(0);
      setRateSource("none");
      return;
    }

    DailyWorkRateProfileRepository.profileRate({
      payer: day.assigned_org?.id ?? day.daily_work_data.vendor.id,
      payee: { worker: individualId },
      service: day.service.id,
    })
      .then(({ rate, source }) => {
        setPayment(rate ?? 0);
        setRateSource(source);
      })
      .catch((error) => {
        console.error("Failed to apply Daily Work profile rate:", error);
        setPayment(0);
        setRateSource("none");
      });
  };

  const handleAssign = async () => {
    if (payment < 0) {
      return;
    }

    try {
      await onAssign({
        individualId: selectedIndividual === "__none__" ? "" : selectedIndividual,
        payment,
      });
      handleClose();
    } catch (error) {
      console.error("Assignment failed:", error);
    }
  };

  const handleUnassign = async () => {
    try {
      await onAssign({
        individualId: "",
        payment: 0,
      });
    } catch (error) {
      console.error("Unassignment failed:", error);
    }
  };

  const isFormValid = (selectedIndividual || selectedIndividual === "__none__") && payment >= 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Assign Individual</DialogTitle>
          <DialogDescription>
            Assign an individual from your organization to this daily work day and set the hourly rate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="individual">Assign Individual</Label>
            {loadingIndividuals ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="ml-2">Loading individuals...</span>
              </div>
            ) : (
              <Select
                value={selectedIndividual ? selectedIndividual : "__none__"}
                onValueChange={(value) => {
                  const nextValue = value === "__none__" ? "" : value;
                  setSelectedIndividual(nextValue);
                  applyProfileRate(value);
                }}
              >
                <SelectTrigger data-testid="subs-daily-work-individual-trigger">
                  <SelectValue placeholder="Select individual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (Unassign)</SelectItem>
                  {individuals.length > 0 ? (
                    individuals.map((individual) => (
                      <SelectItem
                        key={individual.id}
                        value={individual.id}
                        data-testid={`subs-daily-work-individual-option-${individual.organizationIndividualId}`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {individual.name}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No individuals available</div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment">
              Individual hourly rate <span className="text-muted-foreground">($)</span>
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                id="payment"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={payment}
                data-testid="subs-daily-work-hourly-rate-input"
                onChange={(e) => {
                  setPayment(parseFloat(e.target.value) || 0);
                  setRateSource(e.target.value ? "manual" : "none");
                }}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="subs-daily-work-hourly-rate-source">
              {rateSource === "profile" ? "Profile rate applied" : rateSource === "manual" ? "Manual override" : "No profile rate"}
            </p>
          </div>
        </div>
        <DialogFooter>
          {day.assigned_user && (
            <Button variant="outline" onClick={handleUnassign} disabled={isLoading}>
              Unassign Individual
            </Button>
          )}
          <Button onClick={handleClose} variant="outline" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!isFormValid || isLoading} data-testid="subs-daily-work-assign-save">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign Individual"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
